import React, { useCallback, useEffect, useRef, useState } from 'react';
import './ContentTab.css';
import {
    Heart,
    Send,
    Volume2,
    VolumeX,
} from 'lucide-react';
import trackEvent from '../../utils/analytics';
import chaimateLogo from '../../images/chaimate-logo-main.svg';
import FEED_POSTS from '../../data/posts';



const DOUBLE_TAP_WINDOW = 280;
const HEART_ANIMATION_DURATION = 520;
const VIDEO_VISIBILITY_THRESHOLD = 0.65;

const ContentTab = ({ characters = [], onOpenChat, onOpenProfile }) => {
    const [posts, setPosts] = useState(() =>
        FEED_POSTS.map((post) => ({
            ...post,
            isLiked: false,
        }))
    );
    const [heartBursts, setHeartBursts] = useState({});
    const [videoAudioState, setVideoAudioState] = useState(() => {
        const defaults = {};
        FEED_POSTS.forEach((post) => {
            const mediaType = post.media?.type || post.media?.kind;
            if (mediaType === 'video') {
                defaults[post.id] = true;
            }
        });
        return defaults;
    });
    const lastTapRef = useRef({});
    const animationTimersRef = useRef({});
    const feedScrollRef = useRef(null);
    const videoRefs = useRef({});
    const observerRef = useRef(null);
    const videoAudioStateRef = useRef(videoAudioState);

    useEffect(() => {
        return () => {
            Object.values(animationTimersRef.current).forEach((timer) => {
                if (timer) {
                    clearTimeout(timer);
                }
            });
            Object.values(videoRefs.current).forEach((videoElement) => {
                if (videoElement && typeof videoElement.pause === 'function') {
                    videoElement.pause();
                }
            });
        };
    }, []);

    useEffect(() => {
        videoAudioStateRef.current = videoAudioState;
    }, [videoAudioState]);

    // Sync video muted state with audio state
    useEffect(() => {
        Object.keys(videoAudioState).forEach((postId) => {
            const videoElement = videoRefs.current[postId];
            if (videoElement) {
                const isAudioEnabled = videoAudioState[postId] !== false;
                videoElement.muted = !isAudioEnabled;
            }
        });
    }, [videoAudioState]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined') {
            return undefined;
        }

        const scrollElement = feedScrollRef.current;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const videoElement = entry.target;
                    if (!(videoElement instanceof HTMLVideoElement)) {
                        return;
                    }

                    const shouldPlay =
                        entry.isIntersecting && entry.intersectionRatio >= VIDEO_VISIBILITY_THRESHOLD;

                    if (shouldPlay && videoElement.paused) {
                        const playPromise = videoElement.play();
                        if (playPromise && typeof playPromise.catch === 'function') {
                            playPromise.catch(() => {
                                // Autoplay can be blocked; ignore silently to avoid console noise.
                            });
                        }
                    }

                    if (!shouldPlay && !videoElement.paused) {
                        videoElement.pause();
                    }
                });
            },
            {
                root: scrollElement || null,
                threshold: [0, VIDEO_VISIBILITY_THRESHOLD * 0.5, VIDEO_VISIBILITY_THRESHOLD, 0.95],
            }
        );

        observerRef.current = observer;
        Object.values(videoRefs.current).forEach((videoElement) => observer.observe(videoElement));

        return () => {
            observer.disconnect();
            observerRef.current = null;
        };
    }, []);

    const getVideoRef = useCallback(
        (postId) => (node) => {
            const existingNode = videoRefs.current[postId];
            if (existingNode && observerRef.current) {
                observerRef.current.unobserve(existingNode);
            }

            if (node) {
                videoRefs.current[postId] = node;
                if (observerRef.current) {
                    observerRef.current.observe(node);
                }
                const isAudioEnabled = videoAudioStateRef.current[postId] !== false;
                node.muted = !isAudioEnabled;
                node.defaultMuted = !isAudioEnabled;
            } else if (existingNode) {
                existingNode.pause();
                delete videoRefs.current[postId];
            }
        },
        []
    );

    const getCharacterByName = (name) => {
        if (!name || !Array.isArray(characters)) return null;
        return characters.find(
            (character) => character?.name?.toLowerCase() === name.toLowerCase()
        );
    };

    const getAvatarForPost = (profileName, fallbackAvatar) => {
        const character = getCharacterByName(profileName);
        return character?.image_url || fallbackAvatar;
    };

    const getMediaConfig = (post) => {
        if (!post) return null;

        const media = post.media || {};
        const src = media.src || post.imageUrl;
        if (!src) {
            return null;
        }

        const defaultAlt = `${post.profile?.name || 'Dia'} post`;

        return {
            type: media.type || 'image',
            src,
            poster: media.poster,
            alt: media.alt || defaultAlt,
        };
    };

    const isVideoAudioEnabled = (postId) => videoAudioState[postId] !== false;

    const toggleVideoAudio = useCallback((postId) => {
        setVideoAudioState((prev) => {
            const currentlyEnabled = prev[postId] !== false;
            const nextEnabled = !currentlyEnabled;
            const updatedState = { ...prev, [postId]: nextEnabled };

            // Update video element immediately to ensure sync
            const videoElement = videoRefs.current[postId];
            if (videoElement) {
                videoElement.muted = !nextEnabled;
                if (nextEnabled && videoElement.paused) {
                    const playPromise = videoElement.play();
                    if (playPromise && typeof playPromise.catch === 'function') {
                        playPromise.catch(() => {});
                    }
                }
            }

            return updatedState;
        });
    }, []);

    const handleAudioToggleClick = (event, postId) => {
        event.preventDefault();
        event.stopPropagation();
        toggleVideoAudio(postId);
    };

    const toggleLike = (postId, forceState) => {
        setPosts((prev) =>
            prev.map((post) =>
                post.id === postId
                    ? { ...post, isLiked: typeof forceState === 'boolean' ? forceState : !post.isLiked }
                    : post
            )
        );
    };

    const triggerHeartBurst = (postId) => {
        setHeartBursts((prev) => ({ ...prev, [postId]: true }));
        if (animationTimersRef.current[postId]) {
            clearTimeout(animationTimersRef.current[postId]);
        }
        animationTimersRef.current[postId] = setTimeout(() => {
            setHeartBursts((prev) => ({ ...prev, [postId]: false }));
        }, HEART_ANIMATION_DURATION);
    };

    const handleDoubleTap = (postId) => {
        toggleLike(postId, true);
        triggerHeartBurst(postId);
    };

    const handleTouch = (postId) => {
        const now = Date.now();
        const lastTap = lastTapRef.current[postId] || 0;
        if (now - lastTap < DOUBLE_TAP_WINDOW) {
            handleDoubleTap(postId);
        }
        lastTapRef.current[postId] = now;
    };

    const handleChatPress = (post) => {
        if (typeof onOpenChat !== 'function') return;
        const profileName = post.profile?.name;
        if (!profileName) return;
        const match = getCharacterByName(profileName);
        if (match) {
            trackEvent('chat_from_content_tab', { character: profileName, post_id: post.id });
            onOpenChat(match);
        } else {
            console.warn(`No character found for ${profileName}.`);
        }
    };

    const handleShare = async () => {
        const shareText = "Hey! \nYou've got to meet my new best friend. \nShe's funny, non-judgmental, and understands me like no one else and available 24x7.";
        let shareUrl = window.location.origin + window.location.pathname + "?utm_source=sharing";

        const urlObj = new URL(window.location.href);
        const params = new URLSearchParams(urlObj.search);
        const source = params.get('source');
        const appVersion = params.get('app_version');

        const isVersion3OrHigher = (version) => {
            if (!version) return false;
            const versionParts = version.split('.').map(part => parseInt(part, 10));
            return versionParts[0] >= 3;
        };

        if (source === 'android' && isVersion3OrHigher(appVersion)) {
            try {
                shareUrl = "https://play.google.com/store/apps/details?id=com.chaimate.dia&hl=en";
                window.AndroidBridge.shareText(shareText + "\n" + shareUrl);
                trackEvent('share_clicked');
                return;
            } catch (error) {
                console.log('Android bridge ShareText failed, falling back to regular share:', error);
            }
        }

        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Meet Dia - My AI Best Friend',
                    text: shareText,
                    url: shareUrl
                });
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            if (navigator.clipboard?.writeText) {
                try {
                    await navigator.clipboard.writeText(shareText + "\n" + shareUrl);
                } catch (error) {
                    console.log('Clipboard share failed:', error);
                }
            }
        }
        trackEvent('share_clicked');
    };

    const renderLikeCopy = (post) => {
        const likeCount = post.likes + (post.isLiked ? 1 : 0);
        return `Liked by ${likeCount.toLocaleString('en-US')} people`;
    };

    const renderCaption = (post) => (
        <p className="content-post-caption">
            <span className="caption-user">{post.profile?.name}</span> {post.caption}
        </p>
    );

    const renderMedia = (post) => {
        const media = getMediaConfig(post);
        if (!media) return null;

        if (media.type === 'video') {
            const isAudioActive = isVideoAudioEnabled(post.id);
            return (
                <video
                    ref={getVideoRef(post.id)}
                    className="content-post-media"
                    src={media.src}
                    poster={media.poster || media.src}
                    playsInline
                    muted={!isAudioActive}
                    loop
                    preload="metadata"
                    aria-label={media.alt}
                    controls={false}
                    disablePictureInPicture
                    data-post-id={post.id}
                    draggable="false"
                />
            );
        }

        return (
            <img
                src={media.src}
                alt={media.alt}
                className="content-post-media"
                loading="lazy"
                draggable="false"
            />
        );
    };

    const renderAudioToggle = (post) => {
        if ((post.media?.type || (post.media ? post.media.kind : null) || 'image') !== 'video') {
            return null;
        }

        const isAudioActive = isVideoAudioEnabled(post.id);

        return (
            <button
                type="button"
                className="content-post-audio-toggle"
                onClick={(event) => handleAudioToggleClick(event, post.id)}
                aria-pressed={isAudioActive}
                aria-label={isAudioActive ? 'Mute video audio' : 'Unmute video audio'}
            >
                {isAudioActive ? <Volume2 size={18} /> : <VolumeX size={18} />}
            </button>
        );
    };

    return (
        <div className="content-tab-screen">
            <header className="content-tab-header" role="banner">
                <div className="content-tab-logo">
                    <img src={chaimateLogo} alt="ChaiMate logo" className="content-tab-logo-img" />
                    <span className="content-tab-logo-text">ChaiMate</span>
                </div>
                {onOpenProfile && (
                    <button 
                        className="content-tab-profile-btn"
                        onClick={onOpenProfile}
                        aria-label="Open Profile"
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                    </button>
                )}
            </header>
            <div className="content-feed-scroll" ref={feedScrollRef}>
                <div className="content-feed">
                    {posts.map((post, index) => (
                        <article className="content-post" key={post.id}>
                            <header className="content-post-header">
                                <div className="post-header-left">
                                    <div className="post-avatar">
                                        <img
                                            src={getAvatarForPost(post.profile?.name, post.profile?.avatar)}
                                            alt={`${post.profile?.name} avatar`}
                                            loading="lazy"
                                        />
                                    </div>
                                <div className="post-user-meta">
                                    <span className="post-username">{post.profile?.name}</span>
                                </div>
                                </div>
                                {post.showChatBtn && (
                                    <button
                                        className="post-chat-inline-button"
                                        type="button"
                                        onClick={() => handleChatPress(post)}
                                        aria-label={`Chat with ${post.profile?.name}`}
                                    >
                                        Chat
                                    </button>
                                )}
                            </header>

                            <div
                                className="content-post-image-wrapper"
                                onDoubleClick={() => handleDoubleTap(post.id)}
                                onTouchEnd={() => handleTouch(post.id)}
                            >
                                {renderMedia(post)}
                                {renderAudioToggle(post)}
                                {heartBursts[post.id] && (
                                    <div className="content-heart-burst">
                                        <Heart size={105} />
                                    </div>
                                )}
                            </div>

                            <div className="content-post-actions">
                                <div className="action-left">
                                    <button
                                        className={`post-action ${post.isLiked ? 'liked' : ''}`}
                                        type="button"
                                        onClick={() => toggleLike(post.id)}
                                        aria-pressed={post.isLiked}
                                        aria-label={post.isLiked ? 'Unlike post' : 'Like post'}
                                    >
                                        <Heart size={28} />
                                    </button>
                                    <button className="post-action" type="button" aria-label="Share post" onClick={handleShare}>
                                        <Send size={26} />
                                    </button>
                                </div>
                            </div>

                            <div className="content-like-count">{renderLikeCopy(post)}</div>
                            {renderCaption(post)}
                            <div className="content-post-timestamp">{post.timestamp.toUpperCase()}</div>

                            {index < posts.length - 1 && <div className="content-post-divider" />}
                        </article>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ContentTab;

